<template>
    <div v-if="activeSystem">
        <page-header :tabBar="true" :text="$t('navigation.systems.management')" />

        <page-subnavigation :items="allSystemLinks" />

        <div class="subheader">
            <h2 class="subheader__headline">
                {{ activeSystem.name || activeSystem.serial_number }}
                <a class="link link--gray" @click="showChangeHeadDialog()" v-if="$can('change head', activeSystem)">
                    <i class="icon icon--action-edit"></i>
                </a>
            </h2>

            <div class="subheader__labels" v-if="activeSystem.labels">
                <SystemLabels :system="activeSystem" />
            </div>
        </div>

        <div class="row row--table row--table--until-tablet row--blocks">
            <div class="column-tablet-12">
                <p>
                    <span>{{ $t('fields.serial_number') }}: {{ activeSystem.serial_number }}</span>
                </p>

                <p class="align-center">
                    <router-link :to="'/systems/' + activeSystem.id">
                        <img :src="activeSystem.boiler_image_url" />
                        <br />
                    </router-link>
                </p>
            </div>

            <div class="column-tablet-12">
                <p>
                    {{ activeSystem.address1 }}
                    <br />
                    <span v-if="activeSystem.address2">
                        {{ activeSystem.address2 }}
                        <br />
                    </span>
                    {{ activeSystem.postcode }} {{ activeSystem.city }}
                    <span v-if="activeSystem.region">, {{ activeSystem.region }}</span>
                    <br />
                    <span v-if="activeSystem.country">
                        {{ $t('country_code.' + activeSystem.country) }}
                    </span>
                </p>

                <p>
                    <span class="badge-container" v-tooltip.left="systemStatusContent ? { content: systemStatusContent, offset: 10 } : false">
                        <badge v-if="activeSystem.status === 599">{{ $t('systems.status.inactive') }}</badge>

                        <badge type="success" v-else-if="activeSystem.status === 0 && activeSystem.activated_at">{{ $t('systems.status.activated') }}</badge>

                        <badge type="success" v-else-if="activeSystem.status === 200">{{ $t('systems.status.active') }}</badge>

                        <badge type="error" v-else-if="activeSystem.status === 0 && !activeSystem.activated_at">{{ $t('systems.status.not_activated') }}</badge>

                        <badge type="alert" v-else-if="activeSystem.status >= 400">{{ $t('systems.status.not_connected') }}</badge>
                    </span>

                    <badge type="alert" v-if="activeSystem.certificate != undefined && activeSystem.certificate.status === 'requested'">{{ $t('systems.status.csr_requested') }}</badge>
                    <badge type="alert" v-else-if="activeSystem.certificate != undefined && activeSystem.certificate.status === 'renew'">{{ $t('systems.status.csr_renew') }}</badge>
                    <badge type="success" v-else-if="activeSystem.certificate != undefined && activeSystem.certificate.status === 'issued'">{{ $t('systems.status.csr_issued') }}</badge>

                    <badge type="error" v-if="updateStatus && updateStatus.Running">{{ $t('system.status.update_running') }}</badge>

                    <a @click="reloadStatus()" class="link link--gray link--spinnable slab-zero--small--left" :class="{ spin: reloadingStatus }">
                        <i class="icon icon--action-sync icon--lg"></i>
                    </a>
                </p>

                <div class="menu menu--system row--blocks__rebel">
                    <a class="menu__link" @click="showNotificationsSettingsDialog()" v-if="$can('change notifications', activeSystem)">
                        <i class="menu__link__icon icon icon--common-caret"></i>
                        {{ $t('systems.notification_settings') }}
                    </a>
                    <a class="menu__link" @click="showChangeWebserverDialog()" v-if="$can('change rc', activeSystem)">
                        <i class="menu__link__icon icon icon--common-caret"></i>
                        {{ $t('systems.replace_webserver') }}
                    </a>
                    <a class="menu__link" @click="showChangeOwnerDialog()" v-if="$can('change owner', activeSystem)">
                        <i class="menu__link__icon icon icon--common-caret"></i>
                        {{ $t('systems.change_owner') }}
                    </a>
                </div>
            </div>
        </div>

        <div
            class="row row--table row--table--until-tablet row--blocks align-center"
            v-if="$can('invite resident', activeSystem) || $can('invite operator', activeSystem) || $can('invite partner', activeSystem)"
        >
            <div class="column-tablet-8">
                <a class="button button--large" @click="showInviteDialog('resident')" v-if="$can('invite resident', activeSystem)">
                    <i class="icon icon--action-add"></i>
                    {{ $t('systems.invite_new_resident') }}
                </a>
            </div>

            <div class="column-tablet-8">
                <a class="button button--large" @click="showInviteDialog('operator')" v-if="$can('invite operator', activeSystem)">
                    <i class="icon icon--action-add"></i>
                    {{ $t('systems.invite_new_manager') }}
                </a>
            </div>

            <div class="column-tablet-8">
                <a class="button button--large" @click="showTechnicianInviteDialog()" v-if="$can('invite partner', activeSystem)">
                    <i class="icon icon--action-add"></i>
                    {{ $t('systems.invite_new_partner') }}
                </a>
            </div>
        </div>

        <div class="row slab-mobile--large--bottom">
            <div class="table-wrapper">
                <table class="system-table table table--striped" id="system.management.users">
                    <thead>
                        <tr>
                            <th width="33.333%">{{ $t('systems.permissions') }}</th>
                            <th width="33.333%">{{ $t('systems.email') }}</th>
                            <th width="33.333%">{{ $t('systems.access') }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="user in activeSystem.users" :key="user.pid" class="delete-animation">
                            <td>{{ user.name }} ({{ $t('systems.permission.' + user.permission_text) }})</td>
                            <td>{{ user.email }}</td>
                            <td>
                                <a class="button button--attention" @click="removeUser(user)" v-if="canRemoveUser(user)">
                                    <i class="icon icon--action-decline"></i>
                                    <span>{{ $t('systems.remove') }}</span>
                                </a>
                            </td>
                        </tr>

                        <tr v-if="activeSystem.operator_organization">
                            <td>{{ activeSystem.operator_organization.name }} ({{ $t('systems.permission.operator') }})</td>
                            <td>
                                {{ activeSystem.operator_organization.email ? activeSystem.operator_organization.email : '' }}
                            </td>
                            <td>
                                <a class="button button--attention" @click="removeOrganizationOperator()" v-if="$can('invite operator', activeSystem)">
                                    <i class="icon icon--action-decline"></i>
                                    <span>{{ $t('systems.remove') }}</span>
                                </a>
                            </td>
                        </tr>

                        <tr v-if="activeSystem.service_organization">
                            <td>{{ activeSystem.service_organization.name }} ({{ $t('systems.permission.technician') }})</td>
                            <td>
                                {{ activeSystem.service_organization.email ? activeSystem.service_organization.email : '' }}
                            </td>
                            <td>
                                <a
                                    class="button button--attention"
                                    @click="removeTechnician()"
                                    v-if="($can('invite partner', activeSystem) || $can('remove partner', activeSystem)) && !activeSystem.service_organization.is_base_organization"
                                >
                                    <i class="icon icon--action-decline"></i>
                                    <span>{{ $t('systems.remove') }}</span>
                                </a>
                            </td>
                        </tr>

                        <tr v-for="invitation in activeSystem.invitations" :key="invitation.email + invitation.created_at">
                            <td>
                                <span v-if="invitation.invited_organization">{{ invitation.invited_organization.name }}</span>
                            </td>
                            <td>
                                <span v-if="invitation.invited_organization">
                                    {{ invitation.invited_organization.email }}
                                </span>
                                <span v-else>{{ invitation.email }}</span>
                            </td>
                            <td class="text-muted">
                                {{ $t('systems.invitation_not_confirmed') }}
                                <a class="button button--attention button--space-left" @click="removeInvitation(invitation)" v-if="canRemoveUser(invitation, 'permission')">
                                    <i class="icon icon--action-decline"></i>
                                    <span>{{ $t('systems.remove') }}</span>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { VTooltip } from 'v-tooltip';
import { mapState, mapGetters } from 'vuex';
import startCase from 'lodash/startCase';
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import Badge from '@/components/Badge';
import SystemLabels from '@/components/SystemLabels';
import * as moment from 'moment';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    name: 'system-management',
    components: {
        PageHeader,
        Badge,
        SystemLabels,
        PageSubnavigation,
    },
    mixins: [generalLinks],
    directives: {
        tooltip: VTooltip,
    },
    data() {
        return {
            reloadingStatus: false,
            updateStatus: false,
            updateStatusInterval: false,
        };
    },
    computed: {
        ...mapState(['activeSystem']),
        ...mapGetters(['userOrganizationId', 'isParentOrganization']),
        isStructureScanning() {
            if (this.scanStatus) {
                return ['running', 'pending'].indexOf(this.scanStatus) >= 0;
            }

            return false;
        },
        systemStatusContent() {
            if (this.activeSystem && this.activeSystem.vpn_status && ((this.activeSystem.status === 0 && this.activeSystem.activated_at) || this.activeSystem.status === 200)) {
                return `
                    ${this.$t('systems.connected_since')}: ${moment.unix(this.activeSystem.vpn_status.connected_since).format('YYYY-MM-DD')}
                    <br>
                    ${this.$t('systems.sent_data')}: ${this.activeSystem.vpn_status.bytes_sent}
                    <br>
                    ${this.$t('systems.received_data')}: ${this.activeSystem.vpn_status.bytes_received}
                    <br>
                    ${this.$t('fields.enabled_at')}: ${moment(this.activeSystem.activated_at).format('YYYY-MM-DD')}
                `;
            } else if (this.activeSystem && this.activeSystem.activated_at) {
                return `
                    ${this.$t('fields.enabled_at')}: ${moment(this.activeSystem.activated_at).format('YYYY-MM-DD')}
                `;
            }
            return false;
        },
    },
    watch: {
        activeSystem() {
            this.fetchUpdateStatus();
        },
    },
    created() {
        this.fetchUpdateStatus();
    },
    beforeDestroy() {
        if (this.updateStatusInterval) clearInterval(this.updateStatusInterval);
    },
    methods: {
        reloadActiveSystem() {
            this.$store.dispatch('loadActiveSystem', { id: this.activeSystem.id });
        },
        showChangeHeadDialog() {
            this.$modal.show(
                () => import('@/views/modal/SystemChangeHead.vue'),
                {
                    system: this.activeSystem,
                    successCallback: () => {
                        this.reloadActiveSystem();
                    },
                },
                {
                    name: 'SystemChangeHead',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        async reloadStatus() {
            this.reloadingStatus = true;
            try {
                const statusResponse = await api.systems.status({ id: this.activeSystem.id });
                if (statusResponse) {
                    this.$store.commit('setActiveSystemStatus', statusResponse.data.status);
                    this.$store.commit('setActiveSystemVpnStatus', statusResponse.data.vpn_status);
                    if (statusResponse.data.hydraulics_visualization_url) {
                        this.$store.commit('setActiveSystemHydraulicsVisualizationUrl', statusResponse.data.hydraulics_visualization_url);
                    }
                }
                this.reloadActiveSystem();
                this.reloadingStatus = false;
            } catch (error) {
                this.reloadingStatus = false;
                this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
            }
        },
        async fetchUpdateStatus() {
            if (!this.activeSystem) return;
            try {
                const response = await api.systems.getUpdateStatus(this.activeSystem.id);
                if (response && response.data) {
                    this.updateStatus = response.data;
                    if (response.data.Running && !response.data.Finished && !this.updateStatusInterval) {
                        this.startUpdateStatusInterval();
                    } else if ((!response.data.Running || response.data.Finished) && this.updateStatusInterval) {
                        clearInterval(this.updateStatusInterval);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },
        startUpdateStatusInterval() {
            this.updateStatusInterval = setInterval(() => {
                this.fetchUpdateStatus();
            }, 5000);
        },
        showNotificationsSettingsDialog() {
            this.$modal.show(
                () => import('@/views/modal/SystemNotificationSettings.vue'),
                {
                    system: this.activeSystem,
                },
                {
                    name: 'SystemNotificationSettings',
                    scrollable: true,
                    width: 650,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        showChangeWebserverDialog() {
            this.$modal.show(
                () => import('@/views/modal/SystemChangeWebserver.vue'),
                {
                    system: this.activeSystem,
                    successCallback: () => {
                        this.reloadActiveSystem();
                    },
                },
                {
                    name: 'SystemChangeWebserver',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        showChangeOwnerDialog() {
            this.$modal.show(
                () => import('@/views/modal/SystemChangeOwner.vue'),
                {
                    system: this.activeSystem,
                    successCallback: () => {
                        this.reloadActiveSystem();
                    },
                },
                {
                    name: 'SystemChangeOwner',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        showInviteDialog(permission) {
            this.$modal.show(
                () => import('@/views/modal/SystemInvite.vue'),
                {
                    permission,
                    system: this.activeSystem,
                    successCallback: () => {
                        this.reloadActiveSystem();
                    },
                },
                {
                    name: 'SystemInvite',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        showTechnicianInviteDialog() {
            this.$modal.show(
                () => import('@/views/modal/SystemTechnicianInvitation.vue'),
                {
                    system: this.activeSystem,
                    successCallback: () => {
                        this.reloadActiveSystem();
                    },
                },
                {
                    name: 'SystemTechnicianInvitation',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        removeUser(user) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('systems.remove_user_text'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: startCase(this.$t('systems.remove')),
                    successCallback: () => {
                        api.systems.removeUser({ id: this.activeSystem.id, userId: user.pid }).then(
                            () => {
                                this.reloadActiveSystem();
                            },
                            () => {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('errors.error_occurred'),
                                });
                            }
                        );
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        canRemoveUser(user, comparisonField = 'permission_text') {
            let permissions = this.activeSystem.permissions;

            if (!permissions) return false;

            if (user[comparisonField] === 'resident') {
                // user
                if (permissions.invite_resident) {
                    return true;
                }
            } else if (user[comparisonField] === 'technician') {
                if (permissions.change_service_organization) {
                    return true;
                }
            } else if (user[comparisonField] === 'operator') {
                // manager
                if (permissions.invite_operator) {
                    return true;
                }
            }

            return false;
        },
        removeOrganizationOperator() {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('systems.remove_operator_text'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: startCase(this.$t('systems.remove')),
                    successCallback: () => {
                        api.systems.removeOperatorOrganization(this.activeSystem.id).then(
                            () => {
                                this.reloadActiveSystem();
                            },
                            () => {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('errors.error_occurred'),
                                });
                            }
                        );
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        removeTechnician() {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('systems.remove_partner_text'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: startCase(this.$t('systems.remove')),
                    successCallback: () => {
                        api.systems.removeTechnician(this.activeSystem.id).then(
                            () => {
                                this.reloadActiveSystem();
                            },
                            () => {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('systems.management.errors.could_not_change_partner'),
                                });
                            }
                        );
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        removeInvitation(invitation) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('systems.remove_invitation_text'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: startCase(this.$t('systems.remove')),
                    successCallback: () => {
                        api.systems
                            .removeInvitation({
                                id: this.activeSystem.id,
                                invitationId: invitation.id,
                            })
                            .then(
                                () => {
                                    this.reloadActiveSystem();
                                },
                                () => {
                                    this.$notify({
                                        type: 'error',
                                        text: this.$t('errors.error_occurred'),
                                    });
                                }
                            );
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
    },
};
</script>
